import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Heart, Clock, Play, Loader2, UserPlus, Sparkles, Edit3, ArrowLeft, X, RefreshCw, ChevronsLeft, ChevronsRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api, { apiLongTimeout } from '../config/api';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { encodeId } from '../utils/idEncoder';
import UpgradeModal from '../components/UpgradeModal';

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState({ totalStories: 0, favoriteStories: 0, thisMonth: 0 });
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [storyModal, setStoryModal] = useState({ open: false, step: 'list', profile: null });
  const [generationJobs, setGenerationJobs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [upgradeReason, setUpgradeReason] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);
  const autoGenerateTriggeredRef = useRef(false);
  const [selectedProfileId, setSelectedProfileId] = useState('all');

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [filter, sort, debouncedSearch]);

  useEffect(() => {
    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort, debouncedSearch, searchParams, page, selectedProfileId]);

  // Fetch stats separately (doesn't depend on pagination)
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, []);

  // Handle Stripe success/cancel redirects
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    const clearPaymentParams = () => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('success');
        next.delete('session_id');
        next.delete('canceled');
        return next;
      });
    };

    const verifySession = async (session_id) => {
      try {
        const response = await api.get(`/subscriptions/verify-session?session_id=${session_id}`);
        if (response.data.success) {
          toast.success('Payment completed successfully! Subscription activated.');
        } else {
          toast.warning('Payment was not completed. Please try again.');
        }
      } catch (error) {
        console.error('Error verifying session:', error);
        toast.warning('Payment verification failed. If you were charged, please contact support.');
      } finally {
        clearPaymentParams();
      }
    };

    if (success === 'true' && sessionId) {
      verifySession(sessionId);
    } else if (canceled === 'true') {
      toast.warning('Payment was not completed. Please try again.');
      clearPaymentParams();
    }
  }, [searchParams, setSearchParams]);

  // Sync selected profile from URL on load
  useEffect(() => {
    const profileFromUrl = searchParams.get('profile');
    if (profileFromUrl) {
      setSelectedProfileId(profileFromUrl);
    }
  }, [searchParams]);

  // Auto-generate story when autoGenerate parameter is present and profiles are loaded
  useEffect(() => {
    const autoGenerate = searchParams.get('autoGenerate');
    const profileFromUrl = searchParams.get('profile');
    
    // Reset ref if autoGenerate is not present (user navigated away or parameter was removed)
    if (autoGenerate !== 'true') {
      autoGenerateTriggeredRef.current = false;
      return;
    }
    
    if (autoGenerate === 'true' && profileFromUrl && profiles.length > 0 && !profilesLoading && !autoGenerateTriggeredRef.current) {
      const profile = profiles.find((p) => String(p.id) === String(profileFromUrl));
      if (profile) {
        autoGenerateTriggeredRef.current = true;
        // Remove autoGenerate parameter from URL to prevent re-triggering
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('autoGenerate');
          return next;
        });
        // Trigger story generation
        handleStoryGeneration(profile);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, profiles, profilesLoading, setSearchParams]);

  // Restore generation jobs from localStorage on mount
  useEffect(() => {
    try {
      const savedJobs = localStorage.getItem('storyGenerationJobs');
      if (savedJobs) {
        const jobs = JSON.parse(savedJobs);
        // Filter out old completed jobs (older than 1 hour)
        const now = Date.now();
        const activeJobs = jobs.filter(job => {
          if (job.status === 'completed' && job.completedAt) {
            const completedTime = new Date(job.completedAt).getTime();
            return (now - completedTime) < 3600000; // Keep completed jobs for 1 hour
          }
          return true; // Keep all non-completed jobs
        });
        if (activeJobs.length > 0) {
          setGenerationJobs(activeJobs);
        }
      }
    } catch (error) {
      console.error('Failed to restore generation jobs:', error);
    }
  }, []);

  // Persist generation jobs to localStorage whenever they change
  useEffect(() => {
    try {
      if (generationJobs.length > 0) {
        localStorage.setItem('storyGenerationJobs', JSON.stringify(generationJobs));
      } else {
        localStorage.removeItem('storyGenerationJobs');
      }
    } catch (error) {
      console.error('Failed to save generation jobs:', error);
    }
  }, [generationJobs]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const profileIdFromUrl = searchParams.get('profile');
      const profileId =
        selectedProfileId && selectedProfileId !== 'all' ? selectedProfileId : profileIdFromUrl;
      const url = profileId ? `/stories/profile/${profileId}` : '/stories';
      const params = { 
        filter, 
        sort, 
        search: debouncedSearch,
        page,
        limit: 10
      };
      
      const response = await api.get(url, { params });
      if (response.data.success) {
        setStories(response.data.stories);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to load stories:', error);
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        toast.error(serverMessage);
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Cannot reach the server. Please check your internet connection.');
      } else {
        toast.error('Failed to load stories. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/users/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchProfiles = async () => {
    try {
      setProfilesLoading(true);
      const response = await api.get('/profiles');
      if (response.data.success) {
        setProfiles(response.data.profiles || []);
      }
    } catch (error) {
      toast.error('Failed to load profiles');
    } finally {
      setProfilesLoading(false);
    }
  };

  const handleToggleFavorite = async (storyId, currentStatus) => {
    try {
      const response = await api.patch(`/stories/${storyId}/favorite`);
      if (response.data.success) {
        setStories((prev) =>
          prev.map((story) =>
            story.id === storyId ? { ...story, is_favorite: response.data.isFavorite } : story
          )
        );
        // Update stats to reflect the favorite count change
        fetchStats();
        toast.success(response.data.isFavorite ? 'Added to favorites' : 'Removed from favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const handleCreateStoryClick = () => {
    if (profilesLoading) {
      return;
    }
    if (!profiles.length) {
      toast('Create a profile before starting a story.');
      navigate('/profiles/create');
      return;
    }
    if (selectedProfileId && selectedProfileId !== 'all') {
      const preselected = profiles.find((p) => String(p.id) === String(selectedProfileId));
      if (preselected) {
        // If a specific profile is selected, directly generate the story without showing the popup
        handleStoryGeneration(preselected);
        return;
      }
    }
    // If "All Profiles" is selected, show the profile selection popup
    setStoryModal({ open: true, step: 'list', profile: null });
  };
 
  const handleCreateProfileFromModal = () => {
    setStoryModal({ open: false, step: 'list', profile: null });
    navigate('/profiles/create');
  };

  const getAvatarColor = (name = '') => {
    if (!name) return 'from-purple-400 to-pink-400';
    const colors = [
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-pink-400 to-pink-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
      'from-yellow-400 to-yellow-600',
    ];
    return colors[name.charCodeAt(0) % colors.length];
  };

  const openProfileReview = (profile) => {
    setStoryModal({ open: true, step: 'review', profile });
  };

  const closeStoryModal = () => {
    setStoryModal({ open: false, step: 'list', profile: null });
  };

  const goBackToProfileList = () => {
    setStoryModal({ open: true, step: 'list', profile: null });
  };

  const isProfileGenerating = (profileId) =>
    generationJobs.some(
      (job) =>
        job.profileId === profileId && (job.status === 'queued' || job.status === 'generating')
    );

  const updateJob = (jobId, updates) => {
    setGenerationJobs((prev) =>
      prev.map((job) => (job.id === jobId ? { ...job, ...updates } : job))
    );
  };

  const removeJob = (jobId) => {
    setGenerationJobs((prev) => prev.filter((job) => job.id !== jobId));
    // Also remove from localStorage
    try {
      const savedJobs = localStorage.getItem('storyGenerationJobs');
      if (savedJobs) {
        const jobs = JSON.parse(savedJobs);
        const filtered = jobs.filter((job) => job.id !== jobId);
        if (filtered.length > 0) {
          localStorage.setItem('storyGenerationJobs', JSON.stringify(filtered));
        } else {
          localStorage.removeItem('storyGenerationJobs');
        }
      }
    } catch (error) {
      console.error('Failed to remove job from localStorage:', error);
    }
  };

  const handleRetryStoryGeneration = async (job) => {
    if (!job || !job.profileId) return;
    
    // Reset job to generating state
    updateJob(job.id, {
      status: 'generating',
      message: 'Retrying story generation...',
      error: null,
    });

    try {
      const response = await apiLongTimeout.post('/stories/generate', {
        profileId: job.profileId,
      });
      if (response.data.success) {
        const story = response.data.story;
        updateJob(job.id, {
          status: 'completed',
          message: 'Story ready! Tap to read.',
          storyId: story.id,
          error: null,
        });

        // Auto-remove completed job after 15 seconds
        setTimeout(() => {
          removeJob(job.id);
        }, 15000);

        // Reset to page 1 to show the new story
        // Add story to list immediately (optimistic update)
        setStories((prevStories) => {
          // Remove old version if exists and add new one at the beginning
          const filteredStories = prevStories.filter(s => s.id !== story.id);
          return [story, ...filteredStories];
        });
        
        // Reset to page 1 to show the new story
        setPage(1);
        // Refetch stats to update counts
        fetchStats();
        // Also refetch stories to ensure consistency
        fetchStories();
        toast.success(`Story regenerated successfully!`);
      } else {
        throw new Error(response.data.message || 'Failed to generate story');
      }
    } catch (error) {
      console.error(error);
      
      // Check if upgrade is required
      if (error.response?.status === 403 && error.response?.data?.requiresUpgrade) {
        setUpgradeMessage(error.response.data.message || 'Please upgrade to create more stories.');
        setUpgradeReason(error.response.data.reason || 'FREE_ACCOUNT_LIMIT');
        setShowUpgradeModal(true);
        updateJob(job.id, {
          status: 'failed',
          message: 'Upgrade required',
          error: error.response.data.message,
        });
        return;
      }
      
      let errorMessage = error.response?.data?.message || error.message || 'Could not create story. Please try again.';
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Story generation is taking longer than expected. Please wait a moment and check back, or try again.';
      }
      
      updateJob(job.id, {
        status: 'failed',
        message: 'Story generation failed',
        error: errorMessage,
      });
      toast.error(errorMessage);
    }
  };

  const handleStoryGeneration = async (profile) => {
    if (!profile) return;
    const jobId = `job-${profile.id}-${Date.now()}`;
    setGenerationJobs((prev) => [
      ...prev,
      {
        id: jobId,
        profileId: profile.id,
        profileName: profile.first_name,
        status: 'queued',
        message: 'Preparing profile details...',
      },
    ]);
    closeStoryModal();

    try {
      // Step 1: Generating story with ChatGPT
      updateJob(jobId, { status: 'generating', message: '✨ Creating your magical story with AI...' });
      
      // Small delay to show the first message
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updateJob(jobId, { status: 'generating', message: '📖 Writing your personalized story...' });
      
      const response = await apiLongTimeout.post('/stories/generate', {
        profileId: profile.id,
        voice: profile.voice || 'male',
      });
      
      if (response.data.success) {
        const story = response.data.story;
        
        // Check if image was generated
        if (story.thumbnail) {
          updateJob(jobId, {
            status: 'completed',
            message: '🎨 Story and cover image ready! Tap to read.',
            storyId: story.id,
          });
        } else {
          updateJob(jobId, {
            status: 'completed',
            message: '✨ Story ready! Tap to read.',
            storyId: story.id,
          });
        }

        // Auto-remove completed job after 15 seconds (user can still dismiss manually or click Read Story)
        setTimeout(() => {
          removeJob(jobId);
        }, 15000);
        
        // Format story tags if needed (ensure consistency with fetched stories)
        const formattedStory = {
          ...story,
          tags: typeof story.tags === 'string' ? JSON.parse(story.tags) : (story.tags || []),
        };
        
        // Add story to list immediately (optimistic update)
        setStories((prevStories) => {
          // Add new story at the beginning
          const updatedStories = [formattedStory, ...prevStories];
          return updatedStories;
        });
        
        // Update pagination to reflect new total
        setPagination((prevPagination) => ({
          ...prevPagination,
          total: prevPagination.total + 1,
          pages: Math.ceil((prevPagination.total + 1) / prevPagination.limit),
        }));
        
        // Reset to page 1 to show the new story
        setPage(1);
        // Refetch stats to update counts
        fetchStats();
        // Refetch stories in background to ensure consistency (but user already sees the new story)
        setTimeout(() => {
          fetchStories();
        }, 500);
        toast.success(`New story created for ${profile.first_name}! 🎉`);
      } else {
        throw new Error(response.data.message || 'Failed to generate story');
      }
    } catch (error) {
      console.error(error);
      
      // Check if upgrade is required
      if (error.response?.status === 403 && error.response?.data?.requiresUpgrade) {
        setUpgradeMessage(error.response.data.message || 'Please upgrade to create more stories.');
        setUpgradeReason(error.response.data.reason || 'FREE_ACCOUNT_LIMIT');
        setShowUpgradeModal(true);
        updateJob(jobId, {
          status: 'failed',
          message: 'Upgrade required',
          error: error.response.data.message,
        });
        return;
      }
      
      let errorMessage = error.response?.data?.message || error.message || 'Could not create story. Please try again.';
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Story generation is taking longer than expected. Please wait a moment and check back, or try again.';
      }
      
      updateJob(jobId, {
        status: 'failed',
        message: 'Story generation failed',
        error: errorMessage,
      });
      toast.error(errorMessage);
    }
  };

  // Stories are already filtered by the backend, no need for client-side filtering
  const filteredStories = stories;

  if (loading) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-8 flex-1">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Stories</h1>
            <p className="text-gray-600 text-lg">Discover your child's magical adventures</p>
          </div>
          <div className="flex items-center space-x-6 mt-4 lg:mt-0">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.totalStories}</div>
              <div className="text-sm text-gray-500">Total Stories</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-pink-600">{stats.favoriteStories}</div>
              <div className="text-sm text-gray-500">Favorites</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.thisMonth}</div>
              <div className="text-sm text-gray-500">This Month</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stories..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {['all', 'favorites', 'recent', 'unread'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg border transition-colors capitalize ${
                    filter === f
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {f === 'all' ? 'All Stories' : f}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={selectedProfileId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedProfileId(value);
                  setPage(1);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (value && value !== 'all') {
                      next.set('profile', value);
                    } else {
                      next.delete('profile');
                    }
                    return next;
                  });
                }}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                disabled={profilesLoading}
              >
                <option value="all">All Profiles</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.first_name || 'Profile'} {p.last_name || ''}
                  </option>
                ))}
              </select>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="alphabetical">A-Z</option>
                <option value="favorites">Most Loved</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={handleCreateStoryClick}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New Story
          </button>
        </div>

        {generationJobs.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm uppercase tracking-[0.4em] text-purple-400">In progress</p>
                <h2 className="text-xl font-semibold text-gray-900">Story generation queue</h2>
              </div>
              <span className="text-sm text-gray-500">
                {generationJobs.filter((job) => job.status !== 'completed').length} active
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {generationJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white border border-purple-100 rounded-2xl p-5 shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-[0.3em]">
                        {job.profileName}
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {job.status === 'completed'
                          ? 'Story ready'
                          : job.status === 'failed'
                          ? 'Needs attention'
                          : 'Crafting magic...'}
                      </p>
                    </div>
                    {job.status === 'completed' ? (
                      <button
                        onClick={() => removeJob(job.id)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : job.status === 'failed' ? (
                      <button
                        onClick={() => handleRetryStoryGeneration(job)}
                        className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors shadow-md"
                        title="Retry story generation"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    ) : (
                      <Loader2 className="h-6 w-6 text-purple-600 animate-spin" />
                    )}
                  </div>
                  <p
                    className={`text-sm mt-2 ${
                      job.status === 'failed' ? 'text-red-600' : 'text-gray-600'
                    }`}
                  >
                    {job.message}
                  </p>
                  {job.error && <p className="text-xs text-red-500 mt-1">{job.error}</p>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {job.status === 'completed' && (
                      <>
                        <button
                          onClick={async () => {
                            // Check subscription status before navigating
                            try {
                              const statusResponse = await api.get('/users/subscription-status');
                              if (statusResponse.data.success && statusResponse.data.isFreeAccount) {
                                // Free account - show upgrade modal
                                setUpgradeMessage('Free accounts cannot read stories. Please upgrade to read your stories.');
                                setUpgradeReason('FREE_ACCOUNT_LIMIT');
                                setShowUpgradeModal(true);
                                return;
                              }
                              // Paid account - navigate to story
                              removeJob(job.id);
                              navigate(`/stories/${encodeId(job.storyId)}`);
                            } catch (error) {
                              // If check fails, still try to navigate (backend will handle it)
                              removeJob(job.id);
                              navigate(`/stories/${encodeId(job.storyId)}`);
                            }
                          }}
                          className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
                        >
                          <Sparkles className="h-4 w-4 mr-1 inline" />
                          Read Story
                        </button>
                        <button
                          onClick={() => removeJob(job.id)}
                          className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium"
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                    {job.status === 'failed' && (
                      <button
                        onClick={() => handleRetryStoryGeneration(job)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
                      >
                        <RefreshCw className="h-4 w-4 mr-1 inline" />
                        Retry Generate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredStories.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Play className="h-12 w-12 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">No Stories Found</h3>
              <p className="text-gray-600 mb-8">Start creating magical adventures for your child!</p>
              <button
                type="button"
                onClick={handleCreateStoryClick}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Your First Story
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredStories.map((story) => (
              <div
                key={story.id}
                className="bg-white rounded-xl overflow-hidden border border-purple-100 shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Story Image/Thumbnail */}
                  <div className="md:w-1/3 lg:w-2/5 h-48 md:h-56 lg:h-64 relative overflow-hidden bg-gradient-to-br from-purple-100 via-pink-100 to-yellow-100">
                    {story.thumbnail ? (
                      <img
                        src={story.thumbnail}
                        alt={story.title}
                        className="w-full h-full object-cover transition-opacity duration-300"
                        loading="lazy"
                        onError={(e) => {
                          // Fallback to gradient background if image fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center p-4">
                          <div className="w-16 h-16 mx-auto mb-2 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                            <Play className="h-8 w-8 text-white" />
                          </div>
                          <p className="text-purple-600 font-semibold text-xs">{story.character_name}</p>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => handleToggleFavorite(story.id, story.is_favorite)}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-sm"
                    >
                      <Heart
                        className={`h-4 w-4 ${
                          story.is_favorite ? 'fill-red-500 text-red-500' : 'text-gray-400'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Story Content */}
                  <div className="md:w-2/3 lg:w-3/5 p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1">
                            {story.title}
                          </h3>
                          <p className="text-gray-500 text-xs mb-2">
                            {new Date(story.created_at).toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      <p className="text-gray-600 text-xs leading-relaxed mb-3 line-clamp-3">
                        {story.content?.substring(0, 200)}
                        {story.content?.length > 200 ? '...' : ''}
                      </p>

                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {story.duration || 5} min read
                        </span>
                        {story.theme && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {story.theme}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      <button
                        onClick={async () => {
                          // Check subscription status before navigating
                          try {
                            const statusResponse = await api.get('/users/subscription-status');
                            if (statusResponse.data.success && statusResponse.data.isFreeAccount) {
                              // Free account - show upgrade modal
                              setUpgradeMessage('Free accounts cannot read stories. Please upgrade to read your stories.');
                              setUpgradeReason('FREE_ACCOUNT_LIMIT');
                              setShowUpgradeModal(true);
                              return;
                            }
                            // Paid account - navigate to story
                            navigate(`/stories/${encodeId(story.id)}`);
                          } catch (error) {
                            // If check fails, still try to navigate (backend will handle it)
                            navigate(`/stories/${encodeId(story.id)}`);
                          }
                        }}
                        className="text-purple-600 hover:text-purple-700 font-semibold text-xs underline decoration-2 underline-offset-2 transition-colors"
                      >
                        Read story →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="mt-8 mb-6">
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Pagination Info - Left Side */}
                <div className="text-sm text-gray-700">
                  Showing <span className="font-semibold text-purple-600">{((page - 1) * pagination.limit) + 1}</span> to{' '}
                  <span className="font-semibold text-purple-600">{Math.min(page * pagination.limit, pagination.total)}</span> of{' '}
                  <span className="font-semibold text-pink-600">{pagination.total}</span> stories
                </div>

                {/* Pagination Controls - Right Side */}
                {pagination.pages > 0 && (
                  <div className="flex items-center gap-1">
                    {/* First Page Button - Only show if more than 1 page */}
                    {pagination.pages > 1 && (
                      <button
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                          page === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                        }`}
                        title="First page"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </button>
                    )}

                    {/* Previous Button - Only show if more than 1 page */}
                    {pagination.pages > 1 && (
                      <button
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        disabled={page === 1}
                        className={`px-3 py-2 rounded-lg font-medium transition-all ${
                          page === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                        }`}
                      >
                        Previous
                      </button>
                    )}

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {/* Show page numbers based on current page and total pages */}
                      {(() => {
                        const pages = [];
                        const totalPages = pagination.pages;
                        const currentPage = page;

                        // Always show page 1
                        if (totalPages > 0) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => setPage(1)}
                              className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                                page === 1
                                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                  : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                              }`}
                            >
                              1
                            </button>
                          );
                        }

                        // Show pages around current page
                        if (totalPages > 1) {
                          // Calculate which pages to show
                          let startPage = Math.max(2, currentPage - 1);
                          let endPage = Math.min(totalPages - 1, currentPage + 1);

                          // Adjust if we're near the start or end
                          if (currentPage <= 3) {
                            endPage = Math.min(4, totalPages - 1);
                          }
                          if (currentPage >= totalPages - 2) {
                            startPage = Math.max(totalPages - 3, 2);
                          }

                          // Show ellipsis before if needed
                          if (startPage > 2) {
                            pages.push(
                              <span key="ellipsis-start" className="w-10 h-10 flex items-center justify-center text-purple-600 font-semibold">
                                ...
                              </span>
                            );
                          }

                          // Show page numbers
                          for (let i = startPage; i <= endPage; i++) {
                            if (i !== 1 && i !== totalPages) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => setPage(i)}
                                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                                    page === i
                                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                      : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                                  }`}
                                >
                                  {i}
                                </button>
                              );
                            }
                          }

                          // Show ellipsis after if needed
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="ellipsis-end" className="w-10 h-10 flex items-center justify-center text-purple-600 font-semibold">
                                ...
                              </span>
                            );
                          }

                          // Always show last page if more than 1 page
                          if (totalPages > 1) {
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => setPage(totalPages)}
                                className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                                  page === totalPages
                                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                    : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                                }`}
                              >
                                {totalPages}
                              </button>
                            );
                          }
                        }

                        return pages;
                      })()}
                    </div>

                    {/* Next Button - Only show if more than 1 page */}
                    {pagination.pages > 1 && (
                      <button
                        onClick={() => setPage(prev => Math.min(pagination.pages, prev + 1))}
                        disabled={page === pagination.pages}
                        className={`px-3 py-2 rounded-lg font-medium transition-all ${
                          page === pagination.pages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                        }`}
                      >
                        Next
                      </button>
                    )}

                    {/* Last Page Button - Only show if more than 1 page */}
                    {pagination.pages > 1 && (
                      <button
                        onClick={() => setPage(pagination.pages)}
                        disabled={page === pagination.pages}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                          page === pagination.pages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                        }`}
                        title="Last page"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />

      {storyModal.open && (
        <Modal
          title={storyModal.step === 'review' ? 'Review profile' : 'Choose a hero'}
          onClose={closeStoryModal}
          width="max-w-5xl"
        >
          {storyModal.step === 'review' && storyModal.profile ? (
            <>
              <button
                onClick={goBackToProfileList}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to all profiles
              </button>
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-6">
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getAvatarColor(
                        storyModal.profile.first_name || ''
                      )} text-white text-2xl font-bold flex items-center justify-center`}
                    >
                      {(storyModal.profile.first_name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm uppercase tracking-[0.5em] text-purple-400">
                        Story profile
                      </p>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {storyModal.profile.first_name}'s world
                      </h3>
                      <p className="text-gray-500">
                        Review the details before we craft the next adventure.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { label: 'Age', value: storyModal.profile.age || '—' },
                      { label: 'Country', value: storyModal.profile.country || '—' },
                      { label: 'Language', value: storyModal.profile.language || 'English' },
                      { label: 'Story tone', value: storyModal.profile.tone || 'Entertaining' },
                      { label: 'Ending', value: storyModal.profile.ending || 'Happy' },
                      { label: 'Voice', value: storyModal.profile.voice || 'Male' },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-2xl p-4"
                      >
                        <p className="text-xs uppercase tracking-[0.3em] text-purple-400">
                          {item.label}
                        </p>
                        <p className="text-lg font-semibold text-gray-900">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Favorite themes</p>
                    <div className="flex flex-wrap gap-2">
                      {(storyModal.profile.themes || []).length > 0 ? (
                        storyModal.profile.themes.map((theme) => (
                          <span
                            key={theme}
                            className="px-3 py-1 text-sm rounded-full bg-purple-100 text-purple-700 font-medium"
                          >
                            {theme}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">No themes selected yet</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Interests</p>
                    <div className="flex flex-wrap gap-2">
                      {(storyModal.profile.interests || []).length > 0 ? (
                        storyModal.profile.interests.map((interest) => (
                          <span
                            key={interest}
                            className="px-3 py-1 text-sm rounded-full bg-pink-100 text-pink-700 font-medium"
                          >
                            {interest}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">No interests added yet</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Important people</p>
                    <div className="flex flex-wrap gap-2">
                      {(storyModal.profile.characters || []).length > 0 ? (
                        storyModal.profile.characters.map((char, index) => (
                          <span
                            key={`${char.character_type}-${index}`}
                            className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700"
                          >
                            {char.name} • {char.character_type}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">
                          Add friends or family to personalize the story
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="lg:w-72 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-3xl p-6 shadow-2xl">
                  <p className="text-sm uppercase tracking-[0.4em] text-white/70 mb-2">
                    Next step
                  </p>
                  <h4 className="text-2xl font-bold mb-2">Create their next story</h4>
                  <p className="text-sm text-white/80 mb-6">
                    We’ll use everything on this profile to craft a brand-new adventure in about 30
                    seconds.
                  </p>
                  <button
                    onClick={() => handleStoryGeneration(storyModal.profile)}
                    disabled={isProfileGenerating(storyModal.profile.id)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-purple-700 rounded-2xl font-semibold shadow-lg hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isProfileGenerating(storyModal.profile.id) ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Crafting story...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Create Story Now
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      closeStoryModal();
                      navigate(`/profiles/${storyModal.profile.id}`);
                    }}
                    className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm text-white/80 border border-white/30 rounded-2xl hover:bg-white/10 transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                    Edit profile
                  </button>
                  <p className="text-xs text-white/70 mt-4">
                    Stories generate in the background so you can start another right away.
                  </p>
                </div>
              </div>
            </>
          ) : profilesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="h-8 w-8 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No profiles yet</h3>
              <p className="text-gray-600 mb-6">
                Create a child profile first to start crafting personalized adventures.
              </p>
              <button
                onClick={handleCreateProfileFromModal}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold"
              >
                Create a Profile
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">Select which child this new story should feature.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profiles.map((profile) => (
                  <button
                    type="button"
                    key={profile.id}
                    onClick={() => openProfileReview(profile)}
                    className="w-full text-left bg-gradient-to-br from-white to-gray-50 border border-purple-100 rounded-2xl p-4 hover:border-purple-400 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div
                        className={`w-14 h-14 rounded-full bg-gradient-to-br ${getAvatarColor(
                          profile.first_name || ''
                        )} flex items-center justify-center text-white text-xl font-bold`}
                      >
                        {(profile.first_name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900">{profile.first_name}</p>
                        <p className="text-sm text-gray-500">
                          Age {profile.age || '—'} • {(profile.themes && profile.themes[0]) || 'Adventure'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{profile.story_count || 0} stories</span>
                      <span>·</span>
                      <span>{profile.favorite_count || 0} favorites</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-center mt-6">
                <button
                  onClick={handleCreateProfileFromModal}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-purple-600 hover:text-purple-800"
                >
                  <UserPlus className="h-4 w-4" />
                  Add another profile
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        message={upgradeMessage}
        reason={upgradeReason}
      />
    </div>
  );
};

export default Stories;

